import axios from 'axios'
import { computed, unref } from 'vue'
import { UseQueryOptions } from '@tanstack/vue-query'

import { storeToRefs } from 'pinia'
import { useContext } from '@/composables/context'
import { tripQueryKeys } from '@/composables/trip'
import {
  useQueryClient,
  useQuery,
  useQueryWithInitialData,
  useMutation,
} from '@/composables/vue-query'
import { documentModule, eventModule } from '@/store'
import { useTransportationStore } from '@/stores/transportation'
import {
  CreateRentalInput,
  FlightStatus,
  isPersistedUITransportation,
  Rental,
  Transportation,
  TransportationCreateInput,
  TransportationUIInput,
  TransportationUpdateInput,
} from '@/types/transportation'
import { MaybeRef } from '@/types/types'
import { LocationSuggestion } from '@/types/trip'

export function useTransportationLocationDetails() {
  const transportationStore = useTransportationStore()
  const { selectedTransportation, allTransportations } =
    storeToRefs(transportationStore)

  const getDetailsFromTransportation = (
    transportation: TransportationUIInput,
  ) => {
    if (!isPersistedUITransportation(transportation)) {
      return { origin: undefined, destination: undefined }
    }

    const oldTransportationData = allTransportations.value
      .get(selectedTransportation.value!.waypointId)
      ?.find((t) => t._id === transportation._id)
    if (!oldTransportationData) {
      return { originDetails: undefined, destinationDetails: undefined }
    }

    const getLocation = (placeId: string | undefined) => {
      if (!placeId) {
        return undefined
      }
      if (oldTransportationData.origin.placeId === placeId) {
        return oldTransportationData.origin
      } else if (oldTransportationData.destination.placeId === placeId) {
        return oldTransportationData.destination
      }
    }

    return {
      originDetails: getLocation(transportation.origin?.location.placeId),
      destinationDetails: getLocation(
        transportation.destination?.location.placeId,
      ),
    }
  }

  return { getDetailsFromTransportation }
}

// Transportation --------------------------------------------------------------

export interface TransportationDetailQueryKeyParams {
  tripId: MaybeRef<string>
  transportationId: MaybeRef<string>
}

export const transportationQueryKeys = {
  all: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.detail(tripId), 'transportations'] as const,
  details: (tripId: MaybeRef<string>) =>
    [...transportationQueryKeys.all(tripId), 'detail'] as const,
  detail: (params: TransportationDetailQueryKeyParams) =>
    [
      ...transportationQueryKeys.details(params.tripId),
      params.transportationId,
    ] as const,
}

export function useCreateTransportation<
  TInput extends TransportationCreateInput,
>(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      transportation: TInput
      waypointId: string
      index?: number
    }) => {
      const { data } = await $axios.post<
        Extract<Transportation, { type: TInput['type'] }>
      >(`/api/trips/${unref(tripId)}/transportations`, {
        ...params,
      })

      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tripQueryKeys.summary(tripId),
      })
    },
  })
}

export function useUpdateTransportation<
  TInput extends TransportationUpdateInput,
>(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      transportation: TInput
      transportationId: string
      waypointId: string
      index?: number
    }) => {
      const { data } = await $axios.patch<
        Extract<Transportation, { type: TInput['type'] }>
      >(
        `/api/trips/${unref(tripId)}/transportations/${params.transportationId}`,
        {
          transportation: params.transportation,
          waypointId: params.waypointId,
          index: params.index,
        },
      )

      return data
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: transportationQueryKeys.detail({
          tripId,
          transportationId: variables.transportationId,
        }),
      })
      await queryClient.invalidateQueries({
        queryKey: tripQueryKeys.summary(tripId),
      })
    },
  })
}

export function useDeleteTransportation(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (transportationId: string) => {
      await $axios.delete(
        `/api/trips/${unref(tripId)}/transportations/${transportationId}`,
      )
    },
    onSuccess: async (_data, transportationId, _context) => {
      await queryClient.invalidateQueries({
        queryKey: transportationQueryKeys.detail({ tripId, transportationId }),
      })
      await queryClient.invalidateQueries({
        queryKey: tripQueryKeys.summary(tripId),
      })
    },
  })
}

export function useUpdateTransportationsLocation(tripId: MaybeRef<string>) {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (params: {
      transportations: {
        transportationId: string
        fieldToUpdate: 'origin' | 'destination'
      }[]
      location: LocationSuggestion
    }) => {
      await $axios.post(
        `/api/trips/${unref(tripId)}/transportations/locations`,
        params,
      )
    },
  })
}

export function useTransportationDocuments(
  transportation: MaybeRef<TransportationUIInput>,
) {
  return computed(() => {
    return documentModule.files.filter(
      (doc) => doc._id && unref(transportation).documents?.includes(doc._id),
    )
  })
}

// Flight Status -----------------------------------------------------------------------------------

export interface FlightStatusDetailQueryKeyParams {
  tripId: MaybeRef<string>
  transportationId?: MaybeRef<string>
}

export const flightStatusQueryKeys = {
  all: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.detail(tripId), 'flightStatus'] as const,
  details: (tripId: MaybeRef<string>) =>
    [...flightStatusQueryKeys.all(tripId), 'detail'] as const,
  detail: (params: FlightStatusDetailQueryKeyParams) =>
    [
      ...flightStatusQueryKeys.details(params.tripId),
      params.transportationId,
    ] as const,
}

export function useRegisterFlightAlert() {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (pararms: {
      tripId: string
      transportationId: string
    }) => {
      const { tripId, transportationId } = pararms
      try {
        const response = await $axios.post<{
          success: boolean
          missingFields?: string[]
          flightNumber?: string
        }>(`/api/trips/${tripId}/bookings/${transportationId}/flights`)

        eventModule.setValues({
          flightNumber: response.data.flightNumber || '',
        })
        eventModule.newMessage('success.transportation.flightStatus.register')
        return { success: true }
      } catch (error) {
        if (
          axios.isAxiosError(error) &&
          error.response &&
          error.response.status === 500 &&
          error.response.data?.missingFields
        ) {
          return {
            success: false,
            missingFields: error.response.data.missingFields as string[],
          }
        }
        throw error
      }
    },
  })
}

export function useFlightStatus(params: {
  tripId: string
  transportationId?: string
  queryOptions?: UseQueryOptions<FlightStatus>
}) {
  const { $axios } = useContext()

  return useQuery({
    queryKey: flightStatusQueryKeys.detail({
      transportationId: params.transportationId,
      tripId: params.tripId,
    }),

    queryFn: async () => {
      const response = await $axios.get<{ flightStatus: FlightStatus }>(
        `/api/trips/${params.tripId}/bookings/${params.transportationId}/flights/status`,
      )

      return response.data.flightStatus
    },
    refetchOnWindowFocus: false,
    ...params.queryOptions,
  })
}

// Rental ----------------------------------------------------------------------

export interface RentalDetailQueryKeyParams {
  tripId: MaybeRef<string>
  rentalId: MaybeRef<string>
}

export const rentalQueryKeys = {
  all: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.detail(tripId), 'rentals'] as const,
  lists: (tripId: MaybeRef<string>) =>
    [...rentalQueryKeys.all(tripId), 'list'] as const,
  details: (tripId: MaybeRef<string>) =>
    [...rentalQueryKeys.all(tripId), 'detail'] as const,
  detail: (params: RentalDetailQueryKeyParams) =>
    [...rentalQueryKeys.details(params.tripId), params.rentalId] as const,
}

export function useCreateRental(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (rental: CreateRentalInput) => {
      const { data } = await $axios.post<Rental>(
        `/api/trips/${unref(tripId)}/rentals`,
        rental,
      )

      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: rentalQueryKeys.lists(tripId),
      })
    },
  })
}

export function useUpdateRental(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (rental: CreateRentalInput & { _id: string }) => {
      const { data } = await $axios.patch<Rental>(
        `/api/trips/${unref(tripId)}/rentals/${rental._id}`,
        rental,
      )

      return data
    },
    onSuccess: async (rental) => {
      await queryClient.invalidateQueries({
        queryKey: rentalQueryKeys.lists(tripId),
      })
      await queryClient.invalidateQueries({
        queryKey: rentalQueryKeys.detail({
          tripId,
          rentalId: rental._id,
        }),
      })
    },
  })
}

export function useDeleteRental(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (rentalId: string) => {
      await $axios.delete(`/api/trips/${unref(tripId)}/rentals/${rentalId}`)
    },
    onSuccess: async (_, rentalId) => {
      await queryClient.invalidateQueries({
        queryKey: rentalQueryKeys.lists(tripId),
      })
      await queryClient.invalidateQueries({
        queryKey: rentalQueryKeys.detail({
          tripId,
          rentalId,
        }),
      })
    },
  })
}

export function useListRentals(tripId: MaybeRef<string>) {
  const { $axios } = useContext()

  return useQueryWithInitialData({
    queryKey: rentalQueryKeys.lists(tripId),
    queryFn: async () => {
      const { data } = await $axios.get<{ items: Rental[] }>(
        `/api/trips/${unref(tripId)}/rentals`,
      )

      return data.items
    },
    initialData() {
      return []
    },
  })
}
